import { SlateElementType, SlateMark } from '@vedalib/rich-text'
import cn from 'classnames'
import { useMemo } from 'react'
import React from 'react'

import { IconButton } from 'components/uiKit/Button'
import Checkbox from 'components/uiKit/Checkbox'
import ColorPicker from 'components/uiKit/ColorPicker'
import Divider from 'components/uiKit/Divider'
import Dropdown, { Tooltip } from 'components/uiKit/Dropdown'
import DropdownMenu from 'components/uiKit/DropdownMenu'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import type { IMenuOption } from 'components/uiKit/Menu'
import Select from 'components/uiKit/Select'
import { stopPropagation } from 'constants/commonConstans'
import { PROJECT_PATHS } from 'constants/paths'
import { hasActiveAiConfig, leftAiTokens } from 'entities/AiConfig/helpers'
import type { AiTextPromptTogglesEnum } from 'gql/__generated__/graphql'
import { useMyInfo } from 'gql/__utils__/useMyInfo'
import { usePathParams } from 'routes/hooks'
import { genColorOptions } from 'services/Branding/constants'
import { useBrandTheme } from 'services/Branding/hooks'
import { t } from 'services/Translation'

import { ALL_RT_CONTROLS } from '../RichTextConstants'
import {
  getUpdateFormat,
  getSetToolbarForm,
  getSelectedFormat,
  useRichTextContext,
  getSetPrompt,
} from '../useRichTextContext'
import HideControl from './HideControl'
import * as s from './Toolbar.module.scss'
import CheckboxTool from './components/CheckboxTool'
import OtherSettings from './components/OtherSettings/OtherSettings'
import SymbolItem from './components/SymbolItem'
import { AI_OPTIONS, marks, symbols, TEXT_TYPE_OPTIONS } from './toolbarOptions'

const MAX_AI_TEXT_LENGTH = 2000

const Toolbar: React.FC = () => {
  const { companyId } = usePathParams(PROJECT_PATHS.editor)
  const theme = useBrandTheme()
  const colorOptions = useMemo(() => genColorOptions(theme), [theme])
  const { profile } = useMyInfo(companyId)
  const company = profile
  const toMatchSelected = (() => {
    try {
      const selection = window.getSelection()
      return (selection?.getRangeAt(0).toString().length || 0) > MAX_AI_TEXT_LENGTH
    } catch (error) {
      return false
    }
  })()
  const disabledAi = leftAiTokens(company?.aiConfigs) <= 0
  const hasAi = hasActiveAiConfig(company?.aiConfigs)
  const overlayMessage = useMemo(() => {
    if (disabledAi) {
      return t('elements.richText.ai.tooltip')
    }

    return t('elements.richText.ai.toMatchSelected')
  }, [disabledAi])
  const selectedFormat = useRichTextContext(getSelectedFormat)
  const onUpdateFormat = useRichTextContext(getUpdateFormat)
  const setToolbarForm = useRichTextContext(getSetToolbarForm)
  const setPrompt = useRichTextContext(getSetPrompt)

  const textTypeValue =
    TEXT_TYPE_OPTIONS.find((item) => selectedFormat[item.value])?.value ||
    SlateElementType.elementDefault

  const onLinkClick = () => setToolbarForm(SlateElementType.link)

  const onCrossLinkClick = () => setToolbarForm(SlateElementType.crossLink)

  const onAnnotationClick = () => setToolbarForm(SlateElementType.annotation)

  const onAiClick = ({
    value,
  }: IMenuOption<AiTextPromptTogglesEnum | 'prepromptGroup' | 'customGroup'>) => {
    setToolbarForm('ai')
    setPrompt(value as AiTextPromptTogglesEnum)
  }

  // stopPropagation - HOTFIX for https://www.notion.so/2413-1885457f94c5812498f2e5ff0fc8f373
  return (
    <div className={s.root} onClick={stopPropagation} data-richtext-bubble-toolbar>
      <HideControl enabled={ALL_RT_CONTROLS} name='blockElement'>
        <Tooltip overlay={t('uiKit.tooltip.typography')} trigger={['hoverWithoutContent']}>
          <Select
            name='textType'
            offset={[4, -10]}
            onChange={(value) => onUpdateFormat(value)}
            options={TEXT_TYPE_OPTIONS}
            size={KitSize.S}
            styleType='ghost'
            value={textTypeValue}
          />
        </Tooltip>
        <Divider indents={{ margin: '0 8px' }} size={KitSize.S} styleType='vertical' />
      </HideControl>
      <HideControl enabled={ALL_RT_CONTROLS} name={['color', 'backgroundColor']}>
        <div className={cn(s.groupControl, s.color)}>
          <HideControl enabled={ALL_RT_CONTROLS} name='color'>
            <Tooltip overlay={t('uiKit.tooltip.textColor')} trigger={['hoverWithoutContent']}>
              <ColorPicker
                icon='otherFont'
                name={SlateMark.color}
                offset={[4, 0]}
                onChange={(value) => onUpdateFormat(SlateMark.color, value)}
                options={colorOptions}
                placement='bottom-start'
                size={KitSize.S}
                value={selectedFormat[SlateMark.color]}
              />
            </Tooltip>
          </HideControl>
          <HideControl enabled={ALL_RT_CONTROLS} name='backgroundColor'>
            <Tooltip overlay={t('uiKit.tooltip.highlightColor')} trigger={['hoverWithoutContent']}>
              <ColorPicker
                icon='otherBg'
                name={SlateMark.backgroundColor}
                offset={[4, 0]}
                onChange={(value) => onUpdateFormat(SlateMark.backgroundColor, value)}
                options={colorOptions}
                placement='bottom-start'
                size={KitSize.S}
                value={selectedFormat[SlateMark.backgroundColor]}
                transparent
              />
            </Tooltip>
          </HideControl>
        </div>
        <Divider indents={{ margin: '0 8px' }} size={KitSize.S} styleType='vertical' />
      </HideControl>

      <HideControl enabled={ALL_RT_CONTROLS} name={marks}>
        <div className={s.groupControl}>
          {marks.map((mark) => (
            <HideControl enabled={ALL_RT_CONTROLS} key={mark} name={mark}>
              <CheckboxTool name={mark} />
            </HideControl>
          ))}
        </div>
        <Divider indents={{ margin: '0 8px' }} size={KitSize.S} styleType='vertical' />
      </HideControl>

      <HideControl enabled={ALL_RT_CONTROLS} name={['link', 'crossLink']}>
        <div className={s.groupControl}>
          <HideControl enabled={ALL_RT_CONTROLS} name='link'>
            <Tooltip overlay={t('uiKit.tooltip.externalLink')} trigger={['hoverWithoutContent']}>
              <Checkbox
                name='link'
                onChange={onLinkClick}
                value={selectedFormat.inline?.type == SlateElementType.link}
                hideCheckbox
              >
                <Icon name='link2' size={KitSize.S} />
              </Checkbox>
            </Tooltip>
          </HideControl>
          <HideControl enabled={ALL_RT_CONTROLS} name='crossLink'>
            <Tooltip overlay={t('uiKit.tooltip.internalLink')} trigger={['hoverWithoutContent']}>
              <Checkbox
                name='crossLink'
                onChange={onCrossLinkClick}
                value={selectedFormat.inline?.type == SlateElementType.crossLink}
                hideCheckbox
              >
                <Icon name='interfaceEssentialAnchor' size={KitSize.S} />
              </Checkbox>
            </Tooltip>
          </HideControl>
        </div>
      </HideControl>
      <HideControl enabled={ALL_RT_CONTROLS} name='code'>
        <CheckboxTool name='code' />
        <Divider indents={{ margin: '0 8px' }} size={KitSize.S} styleType='vertical' />
      </HideControl>
      <HideControl enabled={ALL_RT_CONTROLS} name='annotation'>
        <Tooltip overlay={t('uiKit.tooltip.annotation')} trigger={['hoverWithoutContent']}>
          <Checkbox
            name='annotation'
            onChange={onAnnotationClick}
            value={selectedFormat.inline?.type == SlateElementType.annotation}
            hideCheckbox
          >
            <Icon name='annotation' size={KitSize.S} />
          </Checkbox>
        </Tooltip>
        <Divider indents={{ margin: '0 8px' }} size={KitSize.S} styleType='vertical' />
      </HideControl>
      <HideControl enabled={ALL_RT_CONTROLS} name='insertSymbol'>
        <Dropdown
          offset={[4, 95]}
          overlay={
            <div className={s.symbols}>
              {symbols.map((symbol, index) => (
                <SymbolItem key={index} name={symbol} />
              ))}
            </div>
          }
          placement='bottom-end'
          trigger={['click']}
        >
          {({ open }) => (
            <Tooltip overlay={t('uiKit.tooltip.symbols')} trigger={['hoverWithoutContent']}>
              <IconButton
                active={open}
                icon='builderSymbol'
                name='symbol'
                size={KitSize.S}
                styleType='ghost'
              />
            </Tooltip>
          )}
        </Dropdown>
        <Divider indents={{ margin: '0 8px' }} size={KitSize.S} styleType='vertical' />
      </HideControl>
      {hasAi && (
        <HideControl enabled={ALL_RT_CONTROLS} name='ai'>
          <Tooltip
            disabled={!(disabledAi || toMatchSelected)}
            overlay={overlayMessage}
            placement='top-end'
          >
            <DropdownMenu
              name='AiSelect'
              offset={[4, 50]}
              onClick={onAiClick}
              options={AI_OPTIONS}
              placement='bottom-end'
              size={KitSize.S}
            >
              {({ open }) => (
                <Tooltip overlay={t('uiKit.tooltip.aiRichtextToolbar')}>
                  <IconButton
                    active={open}
                    disabled={disabledAi || toMatchSelected}
                    icon='builderAi'
                    name='aiButton'
                    size={KitSize.S}
                    styleType='ghost'
                    inheritColor
                  />
                </Tooltip>
              )}
            </DropdownMenu>
          </Tooltip>
          <Divider indents={{ margin: '0 8px' }} size={KitSize.S} styleType='vertical' />
        </HideControl>
      )}
      <Dropdown offset={[4, 6]} overlay={<OtherSettings />} placement='bottom-end'>
        {({ open }) => (
          <Tooltip overlay={t('uiKit.tooltip.moreOptions')} trigger={['hoverWithoutContent']}>
            <IconButton
              active={open}
              icon='iconsOtherMore'
              name='otherMore'
              size={KitSize.S}
              styleType='ghost'
            />
          </Tooltip>
        )}
      </Dropdown>
    </div>
  )
}

export default Toolbar
